import { service } from '@ember/service';

import type { CreateRecordProperties, PopupData, Service } from 'webapp-types';
import { Direction, ModelName } from 'webapp/enums/enums';

import { tagColor } from 'webapp/helpers/tag-color';
import TagContextPopupComponent from 'webapp/components/tag/popup-tags-context';

import type Popup from 'webapp/models-custom/popup/base';
import Tag from 'webapp/models-custom/tag';
import type TagRecord from 'webapp/models/tag';

import ModelManagerForStoreService from 'webapp/services/model-manager/for-store';

export interface TagCountList {
	id: number;
	text: string;
	color?: string;
	icon: 'TagFill';
	used: number;
}

export default class ModelManagerTagsService extends ModelManagerForStoreService<TagRecord, Tag> {
	@service('popup') declare popupService: Service.Popup;

	@service('constants') declare constantsService: Service.Constants;

	getTagCountList(tagIdsArray: number[]): TagCountList[] {
		const allTags: TagCountList[] = [];

		const tagCountMap = tagIdsArray.reduce((acc: number[], item: number) => {
			if (!acc[item]) {
				acc[item] = 0;
			}
			acc[item]++;
			return acc;
		}, []);

		this.savedRecords.forEach((tag) => {
			const numericId = +tag.id!;
			if (tagIdsArray.includes(numericId)) {
				allTags.push({
					id: numericId,
					text: tag.name,
					color: tagColor(tag.color),
					icon: 'TagFill',
					used: tagCountMap[numericId] || 0,
				});
			}
		});
		return allTags;
	}

	openTagsContext(attachTo: HTMLElement, data: PopupData.Tag.Context): Popup | undefined {
		let popup;

		if (!this.popupService.isOpen(TagContextPopupComponent)) {
			popup = this.popupService.createContext(TagContextPopupComponent, attachTo, {
				directions: [Direction.UP, Direction.DOWN, Direction.RIGHT, Direction.LEFT],
				popupClass: 'popup-type-tags-context optionspopup',
				allowStacked: true,
			});
			popup.open(data);
		}

		return popup;
	}

	openBatchTagsContext(onApplySelected: PopupData.Tag.Context['onApplySelected']) {
		const attachTo = document.querySelector('.batch-button .optionsbutton__main');

		if (!attachTo) {
			return;
		}

		return this.openTagsContext(attachTo as HTMLElement, {
			model: { tags: [] },
			onApplySelected,
		});
	}

	getRandomColor() {
		const colors = this.constantsService.tagColors || [],
			colorsUsed = this.savedRecords.map((item) => item.color);
		let colorsUnused = colors.filter((color) => {
			return !colorsUsed.includes(color);
		});

		if (colorsUnused.length === 0) {
			colorsUnused = colors;
		}

		return colorsUnused[Math.floor(Math.random() * colorsUnused.length)];
	}

	// ------ For-store ------

	modelName = ModelName.TAG;

	override createNewRecord(properties: CreateRecordProperties<TagRecord> = {}): TagRecord {
		if (!properties.name) {
			properties.name = Tag.DEFAULT_NAME;
		}
		if (!properties.color) {
			properties.color = this.getRandomColor();
		}

		return super.createNewRecord(properties);
	}

	createModel(model: TagRecord): Tag {
		return new Tag({ model });
	}
}

declare module '@ember/service' {
	interface Registry {
		'model-manager/tags': ModelManagerTagsService;
	}
}
